import type { ComponentChildren } from "preact";
import type { SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import type { Action } from "../../lib/actions.ts";
import { countHref } from "../../lib/countLinks.ts";
import { spaceUrl } from "../../lib/paths.ts";
import { location } from "../../lib/router.ts";
import { CountPill } from "../../ui/counts.tsx";
import { EntityGlyph } from "../../ui/EntityGlyph.tsx";
import { PageHeader } from "../../ui/PageHeader.tsx";
import { TabNav } from "../../ui/TabNav.tsx";
import type { ToolbarItem } from "../../ui/Toolbar.tsx";

/** Open issue counts of an entity, of items without one (`none`), or (null) of the space. */
function countsOf(space: SpaceOut, scope: string | null) {
  if (scope === "none") {
    return space.no_entity;
  }
  return scope ? space.entities.find((x) => x.id === scope)?.counts : space.counts;
}

/**
 * Title and tabs of the items section: all items and the entities, or (with `entity`) that entity's
 * items and its fields.
 */
export function ItemsNav(p: {
  space: SpaceOut;
  tab: "items" | "entities" | "map" | "fields" | "issues" | "conversation";
  /** With `entity`: its conversation tab's name (with counts). */
  conversation?: ComponentChildren;
  entity?: { id: string; name: string } | undefined;
  title?: string | undefined;
  /** Its actions as a toolbar's (PageHeader's `actions`), in place of `children`. */
  actions?: readonly ToolbarItem[];
  /** Its actions as data (PageHeader's `buttons`: a native app's bar too). */
  buttons?: readonly Action[];
  children?: ComponentChildren;
}) {
  const e = p.entity;
  // Open errors, warnings and notes (the entity's, items without one's, or the space's), each linking to them.
  const scope = e?.id ?? (location.value.searchParams.get("entity") === "none" ? "none" : null);
  const counts = countsOf(p.space, scope);
  const issuesCounts = counts ? <CountPill counts={counts} href={countHref(p.space, scope)} /> : null;
  const items = spaceUrl(p.space, "/items");
  const issues = spaceUrl(p.space, "/issues");
  const tabs = e
    ? [
        { href: `${items}?entity=${e.id}`, label: t("items.tab.items"), current: p.tab === "items" },
        { href: spaceUrl(p.space, `/entities/${e.id}`), label: t("items.tab.fields"), current: p.tab === "fields" },
        {
          href: `${issues}?entity=${e.id}`,
          label: t("items.tab.issues"),
          current: p.tab === "issues",
          after: issuesCounts,
        },
        {
          href: spaceUrl(p.space, `/entities/${e.id}?tab=conversation`),
          label: p.conversation ?? t("item.tab.conversation"),
          current: p.tab === "conversation",
        },
      ]
    : [
        { href: items, label: t("items.tab.items"), current: p.tab === "items" },
        { href: spaceUrl(p.space, "/entities"), label: t("items.tab.entities"), current: p.tab === "entities" },
        { href: spaceUrl(p.space, "/entities/map"), label: t("items.tab.map"), current: p.tab === "map" },
        { href: issues, label: t("items.tab.issues"), current: p.tab === "issues", after: issuesCounts },
      ];
  return (
    <>
      <PageHeader
        title={p.title ?? e?.name ?? t("items.title_plural")}
        id={e?.id}
        icon={<EntityGlyph icon={e ? p.space.entities.find((x) => x.id === e.id)?.icon : null} size={22} pad={4} />}
        actions={p.actions}
        buttons={p.buttons}
      >
        {p.children}
      </PageHeader>
      <TabNav label={t("items.sections")} tabs={tabs} />
    </>
  );
}
